import React, { useEffect, useState, useCallback } from 'react';
import {
  scoutApi, ScoutTradeStats,
  ScoutInstrument, INSTRUMENTS, INSTRUMENT_DISPLAY,
} from '../../services/api';

export type Scope = 'today' | 'week' | 'all';
export type InstrumentFilter = ScoutInstrument | 'ALL';

interface Props {
  scope: Scope;
  setScope: (s: Scope) => void;
  instrumentFilter: InstrumentFilter;
  setInstrumentFilter: (i: InstrumentFilter) => void;
}

/** KPI cards + scope/instrument filters. Lifted out of ScoutExitReviews so
 * the LLM page can host them above the tabs and the filter state can be
 * shared with the Exit Reviews list below. */
const ScoutKpis: React.FC<Props> = ({ scope, setScope, instrumentFilter, setInstrumentFilter }) => {
  const [stats, setStats] = useState<ScoutTradeStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await scoutApi.getTradeStats(scope, instrumentFilter));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [scope, instrumentFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="exit-stats-bar">
      <div className="exit-stats-scope">
        <span className="trigger-meta trigger-meta-dim">Scope:</span>
        {(['today', 'week', 'all'] as Scope[]).map(s => (
          <button
            key={s}
            type="button"
            className={`filter-btn ${scope === s ? 'active' : ''}`}
            onClick={() => setScope(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span className="trigger-meta trigger-meta-dim" style={{ marginLeft: '1rem' }}>Instrument:</span>
        {(['ALL', ...INSTRUMENTS] as InstrumentFilter[]).map(inst => (
          <button
            key={inst}
            type="button"
            className={`filter-btn ${instrumentFilter === inst ? 'active' : ''}`}
            onClick={() => setInstrumentFilter(inst)}
          >
            {inst === 'ALL' ? 'All' : INSTRUMENT_DISPLAY[inst]}
          </button>
        ))}
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      {stats && (
        <div className="exit-stats-cards">
          <div className="exit-stat-card">
            <div className="exit-stat-label">Trades</div>
            <div className="exit-stat-value">{stats.total_trades}</div>
            <div className="exit-stat-sub">
              {stats.open_count > 0 && <span className="exit-stat-sub-open">{stats.open_count} open</span>}
              {stats.open_count > 0 && stats.closed_count > 0 && <span className="exit-stat-sub-divider"> · </span>}
              {stats.closed_count > 0 && <span>{stats.closed_count} closed</span>}
              {stats.rejected_count > 0 && (
                <>
                  <span className="exit-stat-sub-divider"> · </span>
                  <span style={{ color: '#f1c40f' }}>{stats.rejected_count} rejected</span>
                </>
              )}
            </div>
          </div>
          <div className="exit-stat-card">
            <div className="exit-stat-label">Wins / Losses</div>
            <div className="exit-stat-value">
              <span className="pl-positive">{stats.wins}</span>
              <span className="exit-stat-divider"> / </span>
              <span className="pl-negative">{stats.losses}</span>
            </div>
            <div className="exit-stat-sub">
              {stats.win_rate_pct != null ? `${stats.win_rate_pct.toFixed(1)}% win rate` : 'no closed trades'}
            </div>
          </div>
          <div className="exit-stat-card">
            <div className="exit-stat-label">Realized P/L</div>
            <div className={`exit-stat-value ${stats.realized_pl >= 0 ? 'pl-positive' : 'pl-negative'}`}>
              {stats.realized_pl >= 0 ? '+' : ''}{stats.realized_pl.toFixed(2)}
            </div>
            <div className="exit-stat-sub">
              {stats.avg_pl != null ? `avg ${stats.avg_pl >= 0 ? '+' : ''}${stats.avg_pl.toFixed(2)}/trade` : '—'}
            </div>
          </div>
          <div className="exit-stat-card">
            <div className="exit-stat-label">Best / Worst</div>
            <div className="exit-stat-value">
              <span className="pl-positive">
                {stats.best_pl != null ? `${stats.best_pl >= 0 ? '+' : ''}${stats.best_pl.toFixed(0)}` : '—'}
              </span>
              <span className="exit-stat-divider"> / </span>
              <span className="pl-negative">
                {stats.worst_pl != null ? `${stats.worst_pl >= 0 ? '+' : ''}${stats.worst_pl.toFixed(0)}` : '—'}
              </span>
            </div>
            <div className="exit-stat-sub">per-trade range</div>
          </div>
        </div>
      )}

      {stats && instrumentFilter === 'ALL' && Object.keys(stats.per_instrument || {}).length > 0 && (
        <div className="exit-stats-per-instrument">
          {INSTRUMENTS.map(inst => {
            const row = stats.per_instrument?.[inst];
            if (!row) return null;
            return (
              <div key={inst} className="exit-stats-instrument-row">
                <span className="instrument-badge">{INSTRUMENT_DISPLAY[inst]}</span>
                <span className="trigger-meta">{row.total_trades} trades</span>
                {row.closed_count > 0 && (
                  <>
                    <span className="trigger-meta">
                      <span className="pl-positive">{row.wins}W</span>
                      {' / '}
                      <span className="pl-negative">{row.losses}L</span>
                    </span>
                    <span className={`trigger-meta ${row.realized_pl >= 0 ? 'pl-positive' : 'pl-negative'}`}>
                      {row.realized_pl >= 0 ? '+' : ''}{row.realized_pl.toFixed(2)}
                    </span>
                  </>
                )}
                {row.open_count > 0 && (
                  <span className="trigger-meta exit-stat-sub-open">{row.open_count} open</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScoutKpis;
